import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  Briefcase,
  Building2,
  Calendar,
  IndianRupee,
  AlertCircle,
  ChevronDown,
  X,
} from "lucide-react";

import { getJobs } from "../../../services/JobsService";
import { isStaffViewer } from "../Student/Dashboard/StudentDashboard2.jsx";
import { useAuth } from "../../../context/AuthContext";

import "../Trainer/TrainerDashboard.css";
import "./JobBoard.css";

/* =============================================================
   JOB BOARD (/job-board)

   This page used to list three invented openings — Amazon, Google
   and Dribbble, with randomuser.me portraits for company logos,
   San Francisco addresses and dollar salaries — held in local state
   as fixtures. None of it came from the database, and none of the
   three buttons on it worked: "Save" only called console.log,
   "Post a job" wrote to local state and vanished on refresh, and
   "Apply now" opened the post-a-job form.

   It now reads real openings from /jobprofiles through getJobs, and
   the only action it offers is one that exists.

   Worth knowing: /job-Section is a second, fully built job list with
   the real application flow behind it (JobSection/JobListings.jsx
   calls submitJobApplicationData). This page and that one overlap;
   applying is handed off there rather than reimplemented here.
   ============================================================= */

const PAGE_SIZE = 10;

const initialOf = (text) => {
  const t = String(text || "").trim();
  return t ? t.charAt(0).toUpperCase() : "?";
};

const fmtDay = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/* The API hard-codes status:"active" into its where clause
   (jobprofile.service.js:71), so nothing else ever reaches this page.
   The chip reports what the row says rather than guessing a synonym. */
const isOpen = (job) =>
  String(job?.status || "active").trim().toLowerCase() === "active";

export default function JobBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = isStaffViewer(user);

  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");

  /* The search box types faster than the server answers, so the term
     that is sent is held back until typing stops. */
  const [query, setQuery] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(searchTerm.trim()), 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  const load = useCallback(async (pageNum, term, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await getJobs(pageNum, PAGE_SIZE, term);
      const rows = (res.data || []).filter(Boolean);
      setJobs((prev) => (append ? [...prev, ...rows] : rows));
      const total = Number(res.pagination?.totalPages) || 1;
      setHasMore(Boolean(res.pagination?.hasNext) || pageNum < total);
      setPage(pageNum);
    } catch (err) {
      /* The endpoint answers 404 "Student not found" when the signed-in
         account has no matching row in the students table — which is
         what a trainer or coordinator gets, because the service treats
         anyone who is not a superadmin as a student
         (jobprofile.service.js:64,75). Showing its own words makes that
         diagnosable instead of looking like an empty board. */
      const serverSaid = err?.response?.data?.message;
      setError(serverSaid || "Could not load openings. Please try again.");
      if (!append) setJobs([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(1, query, false); }, [load, query]);

  /* Location and department narrow what the page already has; the
     options are built from it rather than from a fixed list, so they
     can only ever offer a value that is actually present. */
  const locations = useMemo(
    () => [...new Set(jobs.map((j) => j.jobLocation).filter(Boolean))].sort(),
    [jobs],
  );
  // By name: `department` holds ids, `departmentName` is what the API
  // resolved them to (jobprofile.service.js). The options were ids.
  const departments = useMemo(
    () => [...new Set(jobs.map((j) => j.departmentName).filter(Boolean))].sort(),
    [jobs],
  );

  const shown = useMemo(
    () =>
      jobs.filter(
        (j) =>
          (!location || j.jobLocation === location) &&
          (!department || j.departmentName === department),
      ),
    [jobs, location, department],
  );

  const clearFilters = () => {
    setSearchTerm("");
    setLocation("");
    setDepartment("");
  };

  const hasFilters = Boolean(searchTerm || location || department);

  /* =============================================================
     STAFF VIEW — the shared kit, read as a list
     ============================================================= */
  if (isStaff) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <section className="td-tbl td-tbl--jblist">
            <header className="td-tbl-head">
              <div>
                <h5 className="td-tbl-title">Job openings</h5>
                <p className="td-tbl-sub">
                  {shown.length} opening{shown.length === 1 ? "" : "s"} listed
                </p>
              </div>
              <button
                type="button"
                className="td-tbl-action"
                onClick={() => navigate("/post-jobs")}
              >
                Post a job →
              </button>
            </header>

            <div className="td-bar">
              <div className="jb-search">
                <Search size={15} aria-hidden="true" />
                <input
                  type="text"
                  value={searchTerm}
                  placeholder="Search role or organisation"
                  aria-label="Search openings"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <p className="td-tbl-empty">Loading openings…</p>
            ) : error ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <AlertCircle size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">Could not load openings</h6>
                <p className="td-state-text">{error}</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="td-state">
                <span className="td-state-icon">
                  <Briefcase size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">No openings</h6>
                <p className="td-state-text">
                  {hasFilters
                    ? "Nothing matches that search."
                    : "No active openings. The list shows roles with status “active” only."}
                </p>
              </div>
            ) : (
              <>
                {/* Five labels against five tracks in --jblist. */}
                <div className="td-tbl-cols">
                  <span>Role</span>
                  <span>Organisation</span>
                  <span>Location</span>
                  <span>Posted</span>
                  <span>Status</span>
                </div>

                <div className="td-tbl-rows">
                  {shown.map((job) => (
                    <div className="td-tbl-row" key={job.id}>
                      <div className="td-tbl-name">
                        <b>{job.jobName || "Role"}</b>
                        <small>{job.departmentName || "—"}</small>
                      </div>
                      <div className="td-tbl-name">
                        <b>{job.organizationName || "—"}</b>
                        <small>{job.salary || "Salary not stated"}</small>
                      </div>
                      <div className="td-tbl-name">
                        <b>{job.jobLocation || "—"}</b>
                      </div>
                      <div className="td-tbl-name">
                        <b>{fmtDay(job.jobPostedDate) || "—"}</b>
                      </div>
                      <span
                        className={`td-tbl-pill${isOpen(job) ? " td-tbl-pill--live" : ""}`}
                      >
                        {job.status || "Open"}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          {hasMore && !loading && !error && (
            <button
              type="button"
              className="jb-more"
              disabled={loadingMore}
              onClick={() => load(page + 1, query, true)}
            >
              {loadingMore ? "Loading…" : "Show more openings"}
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  }

  /* =============================================================
     STUDENT VIEW — the openings as a LinkedIn job list
     ============================================================= */
  return (
    <div className="dashboard-wrapper">
      <div className="jb">
        <div className="jb-wrap">
          <div>
            <div className="jb-eyebrow">Placements</div>
            <h1 className="jb-title">Job openings</h1>
            <p className="jb-lede">
              Roles shared with SevenMentor students. Opening one takes you to the
              application form.
            </p>
          </div>

          <div className="jb-tools">
            <div className="jb-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                placeholder="Search by role or organisation"
                aria-label="Search openings"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="jb-search-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="jb-filters">
              <div className="jb-field">
                <label htmlFor="jb-loc">Location</label>
                <select
                  id="jb-loc"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  <option value="">All locations</option>
                  {locations.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>

              <div className="jb-field">
                <label htmlFor="jb-dept">Department</label>
                <select
                  id="jb-dept"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  <option value="">All departments</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="jb-list">
              {[0, 1, 2].map((i) => (
                <div className="jb-skel" key={i}>
                  <div className="jb-skel-mark" />
                  <div className="jb-skel-main">
                    <div className="jb-skel-bar jb-skel-bar--third" />
                    <div className="jb-skel-bar" />
                    <div className="jb-skel-bar jb-skel-bar--half" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="jb-state jb-state--bad">
              <span className="jb-state-mark">
                <AlertCircle size={24} aria-hidden="true" />
              </span>
              <h2 className="jb-state-title">Could not load openings</h2>
              <p className="jb-state-text">{error}</p>
              <button
                type="button"
                className="jb-state-btn"
                onClick={() => load(1, query, false)}
              >
                Try again
              </button>
            </div>
          ) : shown.length === 0 ? (
            <div className="jb-state">
              <span className="jb-state-mark">
                <Briefcase size={24} aria-hidden="true" />
              </span>
              <h2 className="jb-state-title">
                {hasFilters ? "Nothing matches" : "No openings right now"}
              </h2>
              <p className="jb-state-text">
                {hasFilters
                  ? "No opening matches that search or filter."
                  : "Openings are shared with particular batches and courses, so this list shows the roles matched to yours. New ones appear as the placement team assigns them."}
              </p>
              {hasFilters && (
                <button type="button" className="jb-state-btn" onClick={clearFilters}>
                  <X size={14} aria-hidden="true" />
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <ul className="jb-list">
                {shown.map((job) => {
                  const posted = fmtDay(job.jobPostedDate);
                  const closes = fmtDay(job.endDate);
                  return (
                    <li key={job.id}>
                      <article className="jb-card">
                        <span className="jb-mark">{initialOf(job.organizationName)}</span>

                        <div className="jb-main">
                          <h2 className="jb-role">{job.jobName || "Role"}</h2>
                          <p className="jb-org">{job.organizationName || "—"}</p>
                          {job.jobLocation && <p className="jb-place">{job.jobLocation}</p>}

                          {job.jobDescription && (
                            <p className="jb-desc">{job.jobDescription}</p>
                          )}

                          <div className="jb-facts">
                            {job.departmentName && (
                              <span>
                                <Building2 size={13} aria-hidden="true" />
                                {job.departmentName}
                              </span>
                            )}
                            {job.departmentName && posted && (
                              <span className="jb-dot" aria-hidden="true">·</span>
                            )}
                            {posted && (
                              <span>
                                <Calendar size={13} aria-hidden="true" />
                                Posted {posted}
                              </span>
                            )}
                            {closes && (
                              <>
                                <span className="jb-dot" aria-hidden="true">·</span>
                                <span>Closes {closes}</span>
                              </>
                            )}
                          </div>

                          <div className="jb-chips">
                            <span
                              className={`jb-chip ${isOpen(job) ? "jb-chip--open" : "jb-chip--shut"}`}
                            >
                              {job.status || "Open"}
                            </span>
                            {job.jobPositionStatus && (
                              <span className="jb-chip">{job.jobPositionStatus}</span>
                            )}
                          </div>
                        </div>

                        <div className="jb-side">
                          {job.salary && (
                            <span className="jb-pay">
                              <IndianRupee size={13} aria-hidden="true" />
                              {job.salary}
                            </span>
                          )}
                          {/* The application form lives on /job-Section, which
                              is the flow that actually submits. */}
                          <button
                            type="button"
                            className="jb-btn"
                            onClick={() => navigate("/job-Section")}
                          >
                            <MapPin size={14} aria-hidden="true" />
                            View and apply
                          </button>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>

              {hasMore && (
                <button
                  type="button"
                  className="jb-more"
                  disabled={loadingMore}
                  onClick={() => load(page + 1, query, true)}
                >
                  {loadingMore ? "Loading…" : "Show more openings"}
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
